import { StorageServiceModule } from 'services/storage/storage.service';
import { ArrayModelServiceModule } from './arrayModel.service';

export const ModelWatchServiceModule = angular
    .module('app.services.data.modelService.modelWatcher', [
        StorageServiceModule,
        ArrayModelServiceModule
    ]).service('modelWatcher', (arrayModel, $window, storageService) => {
        var api = {
            getChanges: getChanges,
            hasChanges: hasChanges,
            reset: reset,
            update: update,
            stash: stash,
            unstash: unstash,
            makeDirty: makeDirty,
            assignWatchers: assignWatchers,
            updateChangedValues: updateChangedValues
        };

        return api;

        /**
         Gets changed fields from model

         @return {Array} fields changed in model
         */
        function getChanges() {
            return this.changedValues;
        }

        /**
         Returns if Model has been changed

         @param {String} field, optional
         @return {Boolean} changes exist for field or entire Model
         */
        function hasChanges(field) {
            if (field && this.changedValues) {
                return this.changedValues.indexOf(field) > -1;
            }
            if (this.changedValues) {
                return this.changedValues.length > 0;
            }
            return false;
        }

        /**
         Resets the model back to either its original value or last updated value
         */
        function reset(field) {
            if (this.changedValues.length === 0) {
                return;
            }

            if (field) {
                if (Array.isArray(field)) {
                    for (let i = 0; i < field.length; i += 1) {
                        this.changedValues.splice(this.changedValues.indexOf(field[i]), 1);
                        resetField.call(this, field[i]);
                    }
                } else {
                    this.changedValues.splice(this.changedValues.indexOf(field), 1);
                    resetField.call(this, field);
                }
            } else {
                let key;
                while ((key = this.changedValues.pop()) != null) {
                    resetField.call(this, key);
                }
            }

            function resetField(key) {
                /**
                 Move down the tree if a child is a model object
                 if array of values, reassign values
                 if array of object, copy over object
                 - fixes some angular copy issues when trying to override array of values
                 */
                if (this.model[key] instanceof Model) {
                    this.model[key].reset();
                } else if (typeof this._base[key] == 'object' && this._base[key] !== null) {
                    if (Array.isArray(this._base[key]) && typeof this._base[key][0] != 'object') {
                        this.model[key] = angular.copy(this._base[key]);
                        arrayModel.overrideArrayFunctions(this, key);
                    } else if (this.model[key] !== null) {
                        angular.copy(this._base[key], this.model[key]);
                    } else {
                        this.model[key] = this._base[key];
                    }
                } else {
                    this.model[key] = this._base[key];
                }
            }
        }

        /**
         Updates either the entire model or a given field, after updating,
         resets will use last update as the return point

         @param {String} field
         */
        function update(field) {
            if (field) {
                if (this.changedValues.indexOf(field) > -1) {
                    var fieldIndex = this.changedValues.indexOf(field);
                    this.changedValues.splice(fieldIndex, 1);
                    if (typeof this._base[field] === 'object') {
                        angular.copy(this.model[field], this._base[field]);
                    } else {
                        this._base[field] = this.model[field];
                    }

                    if (this.changedValues.length === 0 && this.parent != null) {
                        if (this.parent[this.parentName].getChanges().length === 0) {
                            this.parent.update(this.parentName);
                        }
                    }
                }
            } else {
                this.changedValues = [];
                angular.copy(this.model, this._base);
            }
        }

        function makeDirty(field) {
            if (!field) return;

            if (Array.isArray(field)) {
                for (let i = 0; i < field.length; i += 1) {
                    if (Object.prototype.hasOwnProperty.call(this, field[i])
                            && this.changedValues.indexOf(field[i]) === -1) {
                        this.changedValues.push(field[i]);
                    }
                }
            } else {
                if (!Object.prototype.hasOwnProperty.call(this, field)) return;

                if (this.changedValues.indexOf(field) === -1) {
                    this.changedValues.push(field);
                }
            }
        }

        /**
         Modifies changed values by comparing to base object in model

         @param {Object} obj - model object
         @param {String} propName - property name
         */
        function updateChangedValues(obj, propName) {
            if (!angular.equals(obj.model[propName], obj._base[propName])) {
                if (obj.changedValues.indexOf(propName) === -1) {
                    obj.changedValues.push(propName);
                }
            } else {
                var index = obj.changedValues.indexOf(propName);
                if (index > -1) {
                    obj.changedValues.splice(index, 1);
                }
            }

            if (obj.parent) {
                updateChangedValues(obj.parent, obj.parentName);
            }
        }

        /**
         Wrapper to assign watchers to every property in model

         @param {Object} obj - model object to add getters/setters to
         */
        function assignWatchers(obj) {
            Object.keys(obj.model).forEach((i) => {
                assignWatcher(obj, i);
            });
        }

        /**
         Assigns Getters/setters on object property to watch for changed values

         @param {Object} obj - model object
         @param {String} propName - model property name
         */
        function assignWatcher(obj, propName) {
            Object.defineProperty(obj, propName, {
                enumerable: true,
                get() {
                    return obj.model[propName];
                },

                set(newValue) {
                    obj.model[propName] = newValue;
                    if (obj.model[propName] === '') {
                        obj.model[propName] = null;
                    }

                    updateChangedValues(obj, propName);
                    if (Array.isArray(obj.model[propName])) {
                        arrayModel.assignArrayProperty(obj, propName);
                    }

                    if (obj.parent) {
                        updateChangedValues(obj.parent, obj.parentName);
                    }
                }
            });

            if (Array.isArray(obj.model[propName])) {
                arrayModel.assignArrayProperty(obj, propName);
            } else if (typeof obj.model[propName] == 'object' && obj.model[propName] != null) {
                obj.model[propName] = assignParentModel(obj, propName, obj[propName]);
            }
        }

        /**
         Set parent of object to allow for cascading change tracking

         @param {Object} parent - Parent model object
         @param {String} parentName - Parent property
         @param {Object} child - item to assign parent

         @return {Object} Modified child object
         */
        function assignParentModel(parent, parentName, child) {
            if (!(child instanceof Model)) {
                child = new Model(child);
            }

            Object.defineProperty(child, 'parent', {
                value: parent,
                configurable: true
            });
            Object.defineProperty(child, 'parentName', {
                value: parentName,
                configurable: true
            });

            return child;
        }

        function stash() {
            if (!this._stash) {
                this._stash = {};
            }

            this._stash.model = angular.copy(this.model);
            this._stash.changes = angular.copy(this.changedValues);

            if (storageService.supported() && this.model.id) {
                $window.localStorage.setItem(`modelStash_${this.model.id}`, JSON.stringify(this._stash));
            }

            this.reset();
        }

        function unstash() {
            if ((!this._stash || Object.keys(this._stash).length === 0) && storageService.supported()) {
                this._stash = $window.localStorage.getItem(`modelStash_${this.model.id}`);
                this._stash = JSON.parse(this._stash);
            }

            if (storageService.supported()) {
                $window.localStorage.removeItem(`modelStash_${this.model.id}`);
            }

            if (this._stash && Object.keys(this._stash).length !== 0) {
                this.model = angular.copy(this._stash.model);
                this.changedValues = angular.copy(this._stash.changes);
            }
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/data/model/modelWatch.service.js